import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import { track, trackServer } from '../lib/analytics';
import { FLAGS } from '../lib/flags';
import {
    Send, Loader2, BrainCircuit, RotateCcw, MessageSquareText, MessageCircleQuestion,
    Globe, Sparkles, X,
} from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

// Lightweight UI-language resolver. The /ask endpoint accepts a hint so
// the model can respond in the user's language - we sniff the document
// element and the browser as fallbacks.
const resolveUiLanguage = () => {
    try {
        if (typeof document !== 'undefined' && document.documentElement?.lang) {
            return document.documentElement.lang.split('-')[0];
        }
        if (typeof navigator !== 'undefined') {
            const lang = navigator.language || (navigator.languages && navigator.languages[0]);
            if (lang) return lang.split('-')[0];
        }
    } catch (_) { /* noop */ }
    return 'en';
};

/**
 * AskBookmarkInline - Per-bookmark Q&A rendered INLINE on the page (no
 * drawer / overlay). Behaves like a humble dashed "add note" affordance
 * when collapsed; tap it and it expands inline below, pushing the
 * surrounding content (AI Summary, tabs, recommendations) down.
 *
 * Threads are persisted server-side (1:1 per user × bookmark) - we load
 * the existing thread on first expand and POST new messages to the same
 * endpoint as before. Quota enforcement remains server-side (we just
 * react to 402).
 */
export const AskBookmarkInline = ({
    bookmarkId,
    bookmarkTitle,
    onQuotaExceeded,
    onMessagesChange,
    lastQuestion,
}) => {
    // Start expanded by default. Matches the anonymous try-it Ask
    // panel's behaviour introduced Aug 26 2026 — one consistent
    // affordance across signed-out and signed-in states. The coach
    // tooltip below still gates on "never asked here before", so
    // veteran users don't get nagged with the guidance card.
    const [expanded, setExpanded] = useState(true);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loadingThread, setLoadingThread] = useState(false);
    const [sending, setSending] = useState(false);
    const inputRef = useRef(null);
    const scrollRef = useRef(null);
    const { user, authBootstrapReady } = useAuth();
    // Coach-mark visibility. Shown once per user, only when the panel
    // is collapsed AND they haven't asked here before. See
    // `useEffect` below for the exact reveal condition + dedup key.
    const [coachVisible, setCoachVisible] = useState(false);

    // Decide whether to reveal the coach-mark on mount. Runs once we
    // know the user and whether there's an existing thread — showing
    // the coach to someone who has already asked a question would be
    // noise, not guidance.
    //
    // Fix B (Aug 2026): users who signed up via the anonymous try-it
    // flow have already been walked through the product live. Under
    // `flag_claim_path_onboarding` the coach never mounts — no
    // reveal, no timer, no localStorage write — so the render tree
    // is identical to a returning user's. Also gated on
    // `authBootstrapReady` so the 1.2s reveal timer never starts
    // while `/try/claim` is still in-flight (would otherwise fire
    // before `onboarded_via_anon_save` lands on the user object).
    useEffect(() => {
        if (!authBootstrapReady) return;
        if (!user?.user_id) return;
        if (FLAGS.CLAIM_PATH_ONBOARDING && user?.onboarded_via_anon_save) return;
        if (lastQuestion) return; // user has already used the feature
        const key = `bs_ask_bookmark_coach_shown:${user.user_id}`;
        try {
            if (localStorage.getItem(key)) return;
        } catch (_) { return; }
        // Small delay so the page has a beat to settle before a
        // tooltip appears next to the trigger. Feels intentional
        // rather than reactive.
        const t = setTimeout(() => {
            setCoachVisible(true);
            try { track('ask_nudge_shown', { surface: 'bookmark_inline' }); } catch (_) { /* swallow */ }
        }, 1200);
        return () => clearTimeout(t);
    }, [user?.user_id, user?.onboarded_via_anon_save, authBootstrapReady, lastQuestion]);

    // Persist the "seen" flag and hide. Called both from the explicit
    // close button and from clicking the trigger itself (any positive
    // acknowledgement counts — no need to nag on subsequent bookmarks).
    const dismissCoach = useCallback((reason) => {
        setCoachVisible(false);
        try {
            const uid = user?.user_id || user?.id;
            const key = uid ? `bs_ask_bookmark_coach_shown:${uid}` : 'bs_ask_bookmark_coach_shown';
            localStorage.setItem(key, '1');
        } catch (_) { /* quota */ }
        if (reason === 'clicked') {
            try { track('ask_nudge_clicked', { surface: 'bookmark_inline' }); } catch (_) { /* swallow */ }
        }
    }, [user?.user_id, user?.id]);

    // Load persisted thread the first time the user expands. Cheap GET;
    // we don't pre-fetch on mount because most viewers won't open it.
    useEffect(() => {
        if (!expanded || !bookmarkId) return;
        let cancelled = false;
        const load = async () => {
            setLoadingThread(true);
            try {
                const res = await fetch(`${API}/bookmarks/${bookmarkId}/ask`, { credentials: 'include' });
                if (cancelled) return;
                if (res.ok) {
                    const d = await res.json();
                    setMessages(d.messages || []);
                }
            } catch (_) {
                // Silent - empty thread is a fine starting state.
            } finally {
                if (!cancelled) setLoadingThread(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [expanded, bookmarkId]);

    // Bubble messages up so the parent's "Continue: …" hint stays in
    // sync (the trigger label re-renders when the thread changes).
    useEffect(() => {
        if (onMessagesChange) onMessagesChange(messages);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages]);

    // No auto-focus. The panel now renders expanded by default (Aug
    // 2026), so calling `.focus()` on mount would pop the mobile
    // keyboard and push the bookmark title / thumbnail off-screen for
    // every visit. Users can tap the input themselves when they want
    // to ask — the affordance is right there, no keyboard needed
    // until they engage. (Reported Aug 27 2026.)

    // Pin scroll to the bottom of the messages container on every
    // new message / typing indicator. The container itself is the only
    // scroller; the page scroll is left untouched so the user can keep
    // reading the AI summary while the conversation extends downward.
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, sending]);

    const send = useCallback(async (text) => {
        const msg = (text ?? input).trim();
        if (!msg || sending) return;
        // Fire `first_ai_question` — the RAG activation event. Server
        // enforces once-per-user via the DB-level unique index and re-
        // derives from ai_chats + bookmark_chat_messages counts. Client
        // just calls on every ask; server dedupes. See comment in
        // AIChatPage.js for the full rationale.
        try {
            trackServer('first_ai_question', { entry_point: 'bookmark_inline' });
        } catch (_) { /* analytics must never break the chat */ }
        setSending(true);
        // First send dismisses the coach — the user has engaged with
        // the feature, no more need to nudge them toward it.
        if (coachVisible) dismissCoach('clicked');
        const optimisticUser = { role: 'user', content: msg, timestamp: new Date().toISOString() };
        setMessages((prev) => [...prev, optimisticUser]);
        setInput('');
        try {
            const res = await fetch(`${API}/bookmarks/${bookmarkId}/ask`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    message: msg,
                    ui_language: resolveUiLanguage(),
                }),
            });
            if (res.status === 402) {
                const d = await res.json().catch(() => ({}));
                toast.error(d.detail || 'AI quota reached');
                setMessages((prev) => prev.slice(0, -1));
                onQuotaExceeded?.();
                return;
            }
            if (!res.ok) {
                const d = await res.json().catch(() => ({}));
                throw new Error(d.detail || `HTTP ${res.status}`);
            }
            const data = await res.json();
            setMessages(data.messages || []);
        } catch (e) {
            setMessages((prev) => prev.slice(0, -1));
            toast.error(e.message || 'Could not get a response');
        } finally {
            setSending(false);
        }
    }, [input, sending, bookmarkId, onQuotaExceeded, user?.user_id, user?.id]);

    const clearThread = async () => {
        if (!messages.length || sending) return;
        if (!window.confirm('Clear this conversation? Your past messages will be removed (but they still count toward your AI quota for the period).')) return;
        try {
            await fetch(`${API}/bookmarks/${bookmarkId}/ask`, { method: 'DELETE', credentials: 'include' });
            setMessages([]);
            toast.success('Conversation cleared');
        } catch (_) {
            toast.error('Could not clear conversation');
        }
    };

    // ── Collapsed state - dashed border affordance, visually identical
    //    in weight to the "Add a note..." control elsewhere on the
    //    page. Intentionally low-contrast: it should feel like a quiet
    //    option, not an attention-grabbing CTA.
    if (!expanded) {
        const hasThread = !!lastQuestion;
        return (
            <div className="relative mb-6" data-testid="ask-bookmark-collapsed-wrap">
                <button
                    type="button"
                    onClick={() => { setExpanded(true); if (coachVisible) dismissCoach('clicked'); }}
                    className="w-full flex items-center gap-2 p-3 rounded-xl border border-dashed border-border hover:border-primary/30 hover:bg-secondary/30 transition-colors text-sm text-muted-foreground text-left"
                    data-testid="ask-bookmark-trigger"
                    title={hasThread ? `Continue: ${lastQuestion}` : 'Ask BrainSync about this bookmark'}
                >
                    <MessageCircleQuestion className="w-3.5 h-3.5 shrink-0" />
                    {hasThread ? (
                        <span className="truncate flex-1">
                            <span className="font-medium">Continue:</span>{' '}
                            <span className="italic">&ldquo;{lastQuestion}&rdquo;</span>
                        </span>
                    ) : (
                        <span className="flex-1">Ask BrainSync about this bookmark...</span>
                    )}
                </button>

                {/* Coach-mark - a one-time tutorial pointer that
                    highlights the Ask BrainSync feature the first time
                    a user views a bookmark. Styled to match the
                    onboarding `StepBanner`: white/95 backdrop-blur,
                    emerald accent dot, subtle downward pointer. Renders
                    above the trigger and disappears once the user
                    interacts (click, close, or expand). */}
                {coachVisible && (
                    <div
                        className="absolute left-1/2 -translate-x-1/2 -top-2 -translate-y-full w-[min(320px,calc(100%-8px))] flex items-start gap-2.5 bg-white/95 backdrop-blur-md rounded-2xl px-3.5 py-3 shadow-[0_10px_25px_-5px_rgba(0,0,0,0.35)] ring-1 ring-black/5 z-30 animate-in fade-in slide-in-from-bottom-2 duration-300"
                        role="status"
                        data-testid="ask-bookmark-coach"
                    >
                        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 text-sm font-bold flex items-center justify-center shadow-sm">
                            <Sparkles className="w-4 h-4" />
                        </span>
                        <div className="flex-1 min-w-0">
                            <p className="text-[13px] font-semibold text-zinc-900 leading-tight">Try Ask BrainSync</p>
                            <p className="text-[12px] text-zinc-600 leading-snug mt-0.5">Get answers about this bookmark — key takeaways, quick summaries, and more.</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => dismissCoach('closed')}
                            className="flex-shrink-0 -mr-1 -mt-1 p-1 text-zinc-400 hover:text-zinc-700 transition-colors"
                            aria-label="Dismiss tip"
                            data-testid="ask-bookmark-coach-close"
                        >
                            <X className="w-3.5 h-3.5" />
                        </button>
                        {/* Downward pointer connecting to the trigger */}
                        <span
                            aria-hidden="true"
                            className="absolute left-1/2 -translate-x-1/2 -bottom-1.5 w-3 h-3 bg-white/95 backdrop-blur-md rotate-45 ring-1 ring-black/5"
                        />
                    </div>
                )}
            </div>
        );
    }

    // ── Expanded state - inline panel. Lives in normal document flow
    //    so the rest of the page shifts down naturally as the
    //    conversation grows. Internal message list has its own bounded
    //    scroll so very long threads don't push the composer off-screen.
    return (
        <div className="mb-6 relative">
            {/* Coach-mark — first-time-only tooltip pointing at the
                expanded panel. Same content and style as the anonymous
                try-it flow uses, so signed-in and signed-out onboarding
                feel like one product. Auto-dismisses when the user
                sends their first message. */}
            {coachVisible && (
                <div
                    className="absolute left-1/2 -translate-x-1/2 -top-2 -translate-y-full w-[min(320px,calc(100%-8px))] flex items-start gap-2.5 bg-white/95 backdrop-blur-md rounded-2xl px-3.5 py-3 shadow-[0_10px_25px_-5px_rgba(0,0,0,0.35)] ring-1 ring-black/5 z-30 animate-in fade-in slide-in-from-bottom-2 duration-300"
                    role="status"
                    data-testid="ask-bookmark-coach"
                >
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 text-sm font-bold flex items-center justify-center shadow-sm">
                        <Sparkles className="w-4 h-4" />
                    </span>
                    <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-semibold text-zinc-900 leading-tight">Try Ask BrainSync</p>
                        <p className="text-[12px] text-zinc-600 leading-snug mt-0.5">Get answers about this bookmark — key takeaways, quick summaries, and more.</p>
                    </div>
                    <button
                        type="button"
                        onClick={() => dismissCoach('closed')}
                        className="flex-shrink-0 -mr-1 -mt-1 p-1 text-zinc-400 hover:text-zinc-700 transition-colors"
                        aria-label="Dismiss tip"
                        data-testid="ask-bookmark-coach-close"
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                    <span
                        aria-hidden="true"
                        className="absolute left-1/2 -translate-x-1/2 -bottom-1.5 w-3 h-3 bg-white/95 backdrop-blur-md rotate-45 ring-1 ring-black/5"
                    />
                </div>
            )}
            <div
                className="border border-emerald-200/70 bg-white rounded-2xl overflow-hidden shadow-sm"
                data-testid="ask-bookmark-inline"
            >
            {/* Header - compact, identifies the AI surface and offers
                Clear / Hide controls. No bottom divider; the outer
                emerald frame is the only chrome. */}
            <div className="px-3.5 py-2.5 flex items-center gap-2.5 bg-emerald-50/60">
                <div className="relative w-7 h-7 rounded-lg bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shrink-0">
                    <MessageCircleQuestion className="w-3.5 h-3.5 text-white" />
                    <span aria-hidden="true" className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-emerald-400 ring-2 ring-white animate-pulse" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-[13px] text-zinc-900 leading-tight">Ask BrainSync</p>
                    <p className="text-[10.5px] text-zinc-500 truncate" data-testid="ask-bookmark-title">
                        About <span className="text-zinc-700 font-medium">{bookmarkTitle || 'this bookmark'}</span>
                    </p>
                </div>
                {messages.length > 0 && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearThread}
                        className="h-7 px-2 text-xs text-zinc-500 hover:text-zinc-900"
                        data-testid="ask-bookmark-clear"
                        title="Clear conversation"
                    >
                        <RotateCcw className="w-3.5 h-3.5" />
                    </Button>
                )}
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setExpanded(false)}
                    className="h-7 px-2 text-xs text-zinc-500 hover:text-zinc-900"
                    data-testid="ask-bookmark-collapse"
                    aria-label="Hide conversation"
                >
                    Hide
                </Button>
            </div>

            {/* Messages - only rendered when there's something to show
                (or we're actively loading / streaming). Bounded height
                so a 50-message thread doesn't dominate the page. */}
            {(loadingThread || messages.length > 0 || sending) && (
                <div
                    ref={scrollRef}
                    className="max-h-[340px] overflow-y-auto px-3.5 py-3 space-y-2.5"
                    data-testid="ask-bookmark-messages"
                >
                    {loadingThread && (
                        <div className="flex items-center justify-center py-6 text-zinc-400">
                            <Loader2 className="w-4 h-4 animate-spin" />
                        </div>
                    )}
                    {messages.map((m, idx) => (
                        <MessageBubble key={idx} message={m} />
                    ))}
                    {sending && (
                        <div className="flex items-center gap-2 text-xs text-emerald-700/80 py-1" data-testid="ask-bookmark-typing">
                            <BrainCircuit className="w-3.5 h-3.5 text-emerald-600 animate-pulse" />
                            <span className="italic">BrainSync is thinking…</span>
                        </div>
                    )}
                </div>
            )}

            {/* Composer */}
            <div className="px-3 pt-2.5 pb-3 bg-white">
                <div className="flex items-end gap-2 bg-white rounded-xl border border-zinc-200 focus-within:border-emerald-300 focus-within:ring-2 focus-within:ring-emerald-100 transition-all p-1.5">
                    <Textarea
                        ref={inputRef}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                send();
                            }
                        }}
                        placeholder="Ask about this bookmark…"
                        className="flex-1 min-h-[36px] max-h-[120px] rounded-lg resize-none text-sm border-0 focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none bg-transparent py-1.5"
                        data-testid="ask-bookmark-input"
                    />
                    <Button
                        type="button"
                        onClick={() => send()}
                        disabled={!input.trim() || sending}
                        className="h-9 w-9 p-0 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white shrink-0 shadow-sm shadow-emerald-500/30 disabled:bg-zinc-300 disabled:shadow-none transition-all"
                        data-testid="ask-bookmark-send"
                        aria-label="Send"
                    >
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                </div>
                <p className="text-[10.5px] text-zinc-400 mt-1.5 text-center select-none">
                    Off-topic? I&apos;ll send you to a general AI.
                </p>
            </div>
            </div>
        </div>
    );
};

const MessageBubble = ({ message }) => {
    const isUser = message.role === 'user';
    const sources = Array.isArray(message.sources) ? message.sources : [];
    return (
        <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`} data-testid={`ask-bookmark-msg-${message.role}`}>
            <div
                className={`max-w-[88%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap break-words ${
                    isUser
                        ? 'bg-emerald-600 text-white rounded-br-md'
                        : 'bg-zinc-100 text-zinc-900 rounded-bl-md'
                }`}
            >
                {!isUser && (
                    <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wide font-semibold text-emerald-700 mb-1">
                        <MessageSquareText className="w-3 h-3" />
                        BrainSync
                    </div>
                )}
                {message.content}
                {/* Source chips - only rendered when the backend
                    enriched this answer with web research. Domain-only
                    label keeps chips compact; full title lives in
                    title= tooltip for users who want to vet a source
                    before clicking. */}
                {!isUser && sources.length > 0 && (
                    <div className="mt-2.5 pt-2 border-t border-zinc-200/80 flex flex-wrap gap-1.5" data-testid="ask-bookmark-sources">
                        <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-wide text-zinc-500">
                            <Globe className="w-3 h-3" /> Sources
                        </span>
                        {sources.map((s, i) => (
                            <a
                                key={`${s.url}-${i}`}
                                href={s.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={s.title}
                                className="inline-flex max-w-[180px] items-center gap-1 truncate rounded-full bg-white border border-zinc-200 hover:border-emerald-300 hover:text-emerald-700 px-2 py-0.5 text-[11px] text-zinc-700 transition-colors"
                                data-testid={`ask-bookmark-source-${i}`}
                            >
                                <span className="truncate">{s.source}</span>
                            </a>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AskBookmarkInline;
